// "Delivery" on the Red flag warnings tab: who else gets each warning mail.
//
// The mail itself always goes to the employee's official 1Office e-mail — that is not a
// setting. What a project chooses is who is copied (the reporting manager; a fixed list)
// and where a reply goes. A warning names an employee and why they were flagged, so an
// address outside the company is called out in amber before it is saved. The server
// checks every address again; this form only catches the obvious slips early.
//
// Being copied on every warning is a way to read what the mail trail holds, so only a
// person who may see the trail may change these (the server refuses anyone else). Others
// see the saved values, greyed out, and one line saying who can change them.

import { useEffect, useId, useMemo, useState, useRef } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { PageErrorBanner } from '../PageErrorBanner';
import { redFlagWarningMailsAPI, type RedFlagWarningSettings } from '../../services/api';
import { apiMessage, isOutsideCompany, looksLikeAddress, parseAddresses } from './warningMailText';

const MAX_COPIES = 10;

const WHO_CAN_CHANGE =
  'Only super admins and roles that can see all of the project’s data can change who is copied.';

export function WarningDelivery({
  projectId, settings, canEdit, onSaved,
}: {
  projectId: number;
  settings: RedFlagWarningSettings;
  /** false = show the saved values only (the caller may not read the mail trail). */
  canEdit: boolean;
  onSaved: () => void | Promise<void>;
}) {
  const fieldId = useId();
  const [ccManager, setCcManager] = useState(settings.ccManager);
  const [copies, setCopies] = useState(settings.ccAddresses.join(', '));
  const [replyTo, setReplyTo] = useState(settings.replyTo ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Follow the saved values when THEY change (a save, another project) — not on every
  // refresh of the tab, which would wipe what is being typed here.
  const savedKey = JSON.stringify([settings.ccManager, settings.ccAddresses, settings.replyTo || null]);
  // Not on the first run: the fields already start from the saved values, and running
  // again then would wipe a keystroke that landed in the frame after the card appeared.
  const followedKey = useRef(savedKey);
  useEffect(() => {
    if (followedKey.current === savedKey) return;
    followedKey.current = savedKey;
    setCcManager(settings.ccManager);
    setCopies(settings.ccAddresses.join(', '));
    setReplyTo(settings.replyTo ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const addresses = useMemo(() => parseAddresses(copies), [copies]);
  const reply = replyTo.trim();
  const outside = useMemo(() => {
    // One address used for both copies and replies is named once.
    const seen = new Set<string>();
    return [...addresses, ...(reply ? [reply] : [])].filter((a) => {
      if (!looksLikeAddress(a) || !isOutsideCompany(a)) return false;
      const key = a.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [addresses, reply]);
  const dirty = JSON.stringify([ccManager, addresses, reply || null]) !== savedKey;

  const save = async () => {
    setSaved(false);
    const bad = addresses.find((a) => !looksLikeAddress(a));
    if (bad) {
      setError(`“${bad}” is not a valid e-mail address.`);
      return;
    }
    if (addresses.length > MAX_COPIES) {
      setError(`Always copy takes up to ${MAX_COPIES} addresses.`);
      return;
    }
    if (reply && !looksLikeAddress(reply)) {
      setError('Replies go to takes one e-mail address.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await redFlagWarningMailsAPI.saveSettings(projectId, { ccManager, ccAddresses: addresses, replyTo: reply || null });
      setSaved(true);
      await onSaved();
    } catch (e) {
      setError(apiMessage(e, 'Could not save the delivery settings. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="rfw-delivery-title" className="space-y-3">
      <div>
        <h2 id="rfw-delivery-title" className="text-lg font-semibold text-gray-900">Delivery</h2>
        <p className="text-sm text-gray-500">
          Each mail goes to the employee’s official 1Office e-mail. Choose who is copied and where replies go.
        </p>
      </div>
      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={ccManager}
            disabled={!canEdit}
            onChange={(e) => { setCcManager(e.target.checked); setSaved(false); }}
          />
          Copy the reporting manager
        </label>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-copies`} className="mb-1 block text-sm font-medium text-gray-700">Always copy</label>
            <input
              id={`${fieldId}-copies`}
              type="text"
              value={copies}
              disabled={!canEdit}
              onChange={(e) => { setCopies(e.target.value); setSaved(false); }}
              // No example text when read-only: greyed out, it would read as a saved address.
              placeholder={canEdit ? 'name@channelplay.in, another@channelplay.in' : undefined}
              className="form-input disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Copied on every warning mail. Up to {MAX_COPIES} addresses, separated by commas or spaces.
            </p>
          </div>
          <div>
            <label htmlFor={`${fieldId}-reply`} className="mb-1 block text-sm font-medium text-gray-700">Replies go to</label>
            <input
              id={`${fieldId}-reply`}
              type="text"
              value={replyTo}
              disabled={!canEdit}
              onChange={(e) => { setReplyTo(e.target.value); setSaved(false); }}
              placeholder={canEdit ? 'name@channelplay.in' : undefined}
              className="form-input disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              One address. Leave empty to use the standard reply address.
            </p>
          </div>
        </div>

        {outside.length > 0 && (
          <p
            className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="status"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Outside channelplay.in: {outside.join(', ')}. Warning mails name the employee and why they were flagged
              — check that {outside.length === 1 ? 'this address' : 'these addresses'} should receive them.
            </span>
          </p>
        )}

        {error && <PageErrorBanner message={error} />}

        {canEdit ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 btn-brand rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Save
            </button>
            {saved && !dirty && <span className="text-sm text-green-700" role="status">Saved.</span>}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{WHO_CAN_CHANGE}</p>
        )}
      </div>
    </section>
  );
}

export default WarningDelivery;
